/**
 * Centralized API client for Ministry Data Portal frontend.
 * Provides shared base URL, headers, auth token, and consistent error handling.
 */

import { API_BASE_URL } from '../config';

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

let tokenGetter = null;

/**
 * Configure the token getter for authenticated requests.
 * Call from a component inside ClerkProvider: setTokenGetter(() => getToken)
 */
export function setTokenGetter(getter) {
  tokenGetter = getter;
}

/**
 * Low-level request helper. Returns { ok, status, data }.
 * Does not throw for HTTP errors (4xx/5xx) - callers check response.ok/status.
 * Throws for network failures (fetch rejects).
 */
async function request(method, path, body = null) {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const headers = { ...DEFAULT_HEADERS };

  if (tokenGetter) {
    try {
      const token = await tokenGetter();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Ignore token errors; request will fail with 401 if auth required
    }
  }

  const opts = { method, headers };
  if (body !== null && body !== undefined) {
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(url, opts);
  let data = null;
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  }

  return { ok: res.ok, status: res.status, data };
}

/**
 * GET request. Returns { ok, status, data }.
 */
export async function get(path) {
  return request('GET', path);
}

/**
 * POST request. Returns { ok, status, data }.
 */
export async function post(path, body) {
  return request('POST', path, body);
}

/**
 * PATCH request. Returns { ok, status, data }.
 */
export async function patch(path, body) {
  return request('PATCH', path, body);
}

/**
 * PUT request. Returns { ok, status, data }.
 */
export async function put(path, body) {
  return request('PUT', path, body);
}

/**
 * DELETE request. Returns { ok, status, data }.
 * Optional body for APIs that require it (e.g. DELETE with JSON body).
 */
export async function del(path, body = null) {
  return request('DELETE', path, body);
}

/**
 * Extract error message from API response for user display.
 * Uses response.data.message when available, otherwise a generic message.
 */
export function getErrorMessage(response, fallback = 'An error occurred.') {
  if (response?.data?.message) {
    return response.data.message;
  }
  if (response?.status === 404) return 'Not found.';
  if (response?.status >= 500) return 'Server error. Please try again later.';
  return fallback;
}
